import { TrackingUserPosition, TrackingUserPositionAbstract } from '@/types/Tracking'
import moment from 'moment'

class TrackingPositionsVersionsHandler {
  // Constants
  TOTAL_PROPERTIES_REQUIRED_FOR_DEFAULT_VERSION_STRUCTURE = 9
  MAX_ACCURACY_TO_CONSIDER_POSITION = 1000
  MIN_CONFIDENCE_TO_CONSIDER_ACTIVITY_VALID = 80
  MAX_CONFIDENCE_TO_CONSIDER_ACTIVITY_INVALID = 5
  TIME_FORMAT = 'HH:mm:ss'
  TIMESTAMP_FORMAT = 'YYYY-MM-DD HH:mm:ss'

  getValidPositionObject (data: TrackingUserPositionAbstract): TrackingUserPosition {
    let result: TrackingUserPosition

    switch (data.version) {
      case 1:
        result = this.getPositionVersion1(data)
        break
      /**
       * Add more versions here if needed
       */
      default:
        throw new Error('Invalid tracker position - Unssupported version')
    }

    if (!this.validateRequiredData(result)) {
      throw new Error('Invalid tracker position - Missing data - version ' + (data.version || 'default'))
    }

    if (!this.validateAccuracy(result)) {
      throw new Error('Invalid tracker position - Wrong accuracy - version ' + (data.version || 'default'))
    }

    return result
  }

  /**
   * Auxiliar function to validate all processed timestamps are as Zulu time,
   * refering UTC timestamps
   * @param {string} timestamp  - ISO timestamp to validate
   * @returns {string}          - ISO timestamp with Zulu character
   */
  private toUTCZuluTimestamp (timestamp: string): string {
    return timestamp.endsWith('Z') ? timestamp : timestamp + 'Z'
  }

  /**
   * Returns a position object from Version 1 structure
   */
  private getPositionVersion1 (data: any): TrackingUserPosition {
    const dataResult = new TrackingUserPosition()

    // device
    if (data.device) {
      dataResult.device = data.device.name
      dataResult.uuid = data.device.uuid
    }

    // coords
    if (data.coords) {
      dataResult.position = data.coords.latitude && data.coords.longitude ? `${data.coords.latitude},${data.coords.longitude}` : ''
      dataResult.accuracy = data.coords.accuracy
      dataResult.altitude = data.coords.altitude
      dataResult.speed = data.coords.speed
      dataResult.heading = data.coords.heading
    }

    // metadata
    if (data.metadata) {
      dataResult.dateReceiveFB = data.metadata.dateReceiveFB
      dataResult.provider = data.metadata.provider // TODO validation
      dataResult.isMainAppVisible = data.metadata.isMainAppVisible // TODO validation
      dataResult.event = data.metadata.event
      dataResult.mock = data.metadata.mock
      dataResult.odometer = data.metadata.odometer
      dataResult.isMoving = data.metadata.is_moving
      // All timestamps must be assummed as UTC timestamps
      dataResult.time = moment(this.toUTCZuluTimestamp(data.metadata.timestamp)).format(this.TIME_FORMAT)
      dataResult._timestamp = moment(this.toUTCZuluTimestamp(data.metadata.timestamp)).utc()

      if (data.metadata.activity && data.metadata.activity.confidence >= this.MIN_CONFIDENCE_TO_CONSIDER_ACTIVITY_VALID) {
        dataResult.activityConfidence = data.metadata.activity.confidence
        dataResult.activityType = data.metadata.activity.type
      } else if (data.metadata.activity && data.metadata.activity.confidence >= this.MAX_CONFIDENCE_TO_CONSIDER_ACTIVITY_INVALID) {
        dataResult.activityUnconfidence = data.metadata.activity.confidence
        dataResult.activityType = data.metadata.activity.type
      }

      if (data.metadata.battery) {
        dataResult.batteryIsCharging = data.metadata.battery.is_charging
        dataResult.batteryLevel = data.metadata.battery.level
      }

      if (data.metadata.event && data.metadata.event.providerchange) {
        dataResult.event = 'providerchange'
        dataResult._gpsOn = data.metadata.event.providerchange.gps || false
      }
    }

    return dataResult
  }

  /**
   * Valid required data
   */
  private validateRequiredData (data: TrackingUserPosition) {
    return !!data.time && !!data.position
  }

  /**
   * Valid some specific values
   */
  private validateAccuracy (data: TrackingUserPosition) {
    return data.accuracy <= this.MAX_ACCURACY_TO_CONSIDER_POSITION
  }
}

export default new TrackingPositionsVersionsHandler()
